import { useEffect, useMemo, useState } from "react";
import { Calculator, Save, Loader2, CalendarClock, AlertTriangle, Info, FileDown } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, listResource } from "@/lib/api";
import { mxn, fdate, etiquetaPeriodicidad } from "@/lib/format";
import { MoneyInput } from "@/components/common/MoneyInput";
import { AnclaPagoSelect, anclaLabel } from "@/components/common/AnclaPagoSelect";
import { FactorajeSimForm } from "@/components/simulador/FactorajeSimForm";
import { FactorajeSimKpis } from "@/components/simulador/FactorajeSimKpis";
import { TablaFactoraje } from "@/components/creditos/TablaFactoraje";
import { esFactoraje } from "@/lib/factoraje";
import { esArrendamiento } from "@/lib/arrendamiento";
import { ArrendamientoSimForm } from "@/components/simulador/ArrendamientoSimForm";
import { ArrendamientoSimKpis } from "@/components/simulador/ArrendamientoSimKpis";
import { TablaArrendamiento } from "@/components/creditos/TablaArrendamiento";
import { toast } from "sonner";

const SIN_PRODUCTO = "__sin_producto__";

const enDias = (n) => {
  const d = new Date(); d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};

// Factoraje: solo la factura; monto, plazo y periodicidad los resuelve el backend.
const payloadFactoraje = (form, producto) => ({
  producto_id: producto.id,
  valor_nominal: Number(form.valor_nominal),
  aforo_pct: form.aforo_pct === "" ? undefined : Number(form.aforo_pct),
  fecha_vencimiento_factura: form.fecha_vencimiento_factura,
  tasa_ordinaria: Number(form.tasa_ordinaria),
  comision: Number(form.comision),
  comision_apertura_tipo: form.comision_apertura_tipo,
  iva: Number(form.iva),
});

// Arrendamiento: datos del bien; capital, IVA de rentas y forma de cobro los resuelve el backend.
const payloadArrendamiento = (form, producto) => ({
  producto_id: producto.id,
  valor_bien_sin_iva: Number(form.valor_bien_sin_iva),
  enganche: Number(form.enganche || 0),
  residual_pct: form.residual_pct === "" ? undefined : Number(form.residual_pct),
  plazo: Number(form.plazo),
  periodicidad: form.periodicidad,
  tasa_ordinaria: Number(form.tasa_ordinaria),
  comision: Number(form.comision),
  comision_apertura_tipo: form.comision_apertura_tipo,
});

// Helper — determina si un valor está dentro de un rango [min, max]. Rango parcial
// (solo min o solo max) devuelve true para el lado ausente.
const dentroDeRango = (valor, min, max) => {
  const v = Number(valor);
  if (Number.isNaN(v)) return true;
  const mn = min != null && min !== "" ? Number(min) : null;
  const mx = max != null && max !== "" ? Number(max) : null;
  if (mn != null && v < mn) return false;
  if (mx != null && v > mx) return false;
  return true;
};

const formatRangoPct = (min, max) => {
  if (min == null && max == null) return null;
  if (min != null && max != null) return `${min}% – ${max}%`;
  if (min != null) return `≥ ${min}%`;
  return `≤ ${max}%`;
};

const formatRangoMonto = (min, max) => {
  if (min == null && max == null) return null;
  const f = (v) => mxn(v);
  if (min != null && max != null) return `${f(min)} – ${f(max)}`;
  if (min != null) return `≥ ${f(min)}`;
  return `≤ ${f(max)}`;
};

export default function Simulador() {
  const [productos, setProductos] = useState([]);
  const [productoId, setProductoId] = useState(SIN_PRODUCTO);
  const [form, setForm] = useState({
    monto: 100000, plazo: 12, periodicidad: "Mensual", tasa_ordinaria: 48,
    comision: 3, comision_apertura_tipo: "porcentaje", iva: 16, tipo_amortizacion: "francesa_total_fijo",
    redondeo_total_fijo: "centena",
    ancla_pago: "",
    comision_cobro_modo: "descontada",
    valor_nominal: 1000000, aforo_pct: "", fecha_vencimiento_factura: "",
    valor_bien_sin_iva: 1000000, enganche: 0, residual_pct: "",
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [cliente, setCliente] = useState("");

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  // Cargar productos activos de la entidad al montar
  useEffect(() => {
    listResource("productos_credito")
      .then((data) => setProductos((data || []).filter((p) => p.activo !== false)))
      .catch(() => setProductos([]));
  }, []);

  const productoSel = useMemo(
    () => (productoId === SIN_PRODUCTO ? null : productos.find((p) => p.id === productoId) || null),
    [productoId, productos]
  );
  const esFx = esFactoraje(productoSel);
  const esArr = esArrendamiento(productoSel);

  // Al elegir producto: precargar tasa, comisión, tipo de comisión, modo de cobro,
  // tipo de amortización, redondeo, IVA y periodicidad. Usa min como default de tasa
  // (mismo criterio que requisitos.py cuando la solicitud no captura tasa).
  const onProductoChange = (nuevoId) => {
    setProductoId(nuevoId);
    setResult(null);
    if (nuevoId === SIN_PRODUCTO) return;
    const p = productos.find((x) => x.id === nuevoId);
    if (!p) return;
    setForm((prev) => {
      const next = { ...prev };
      const tasaMin = p.tasa_ordinaria_min;
      const tasaUnica = p.tasa_ordinaria;
      if (tasaMin != null && tasaMin !== "") next.tasa_ordinaria = Number(tasaMin);
      else if (tasaUnica != null && tasaUnica !== "") next.tasa_ordinaria = Number(tasaUnica);
      const montoMin = p.monto_min;
      if (montoMin != null && montoMin !== "") next.monto = Number(montoMin);
      if (p.comision_apertura != null && p.comision_apertura !== "") next.comision = Number(p.comision_apertura);
      if (p.comision_apertura_tipo) next.comision_apertura_tipo = String(p.comision_apertura_tipo);
      if (p.comision_cobro_modo) next.comision_cobro_modo = String(p.comision_cobro_modo);
      if (p.tipo_amortizacion) next.tipo_amortizacion = String(p.tipo_amortizacion);
      if (p.redondeo_total_fijo) next.redondeo_total_fijo = String(p.redondeo_total_fijo);
      if (p.iva != null && p.iva !== "") next.iva = Number(p.iva);
      if (p.periodicidad) { next.periodicidad = String(p.periodicidad); next.ancla_pago = ""; }
      if (esFactoraje(p)) {
        next.aforo_pct = p.aforo_pct ?? "";
        next.fecha_vencimiento_factura = enDias(Math.min(60, Number(p.plazo_max_dias_factura) || 60));
      }
      if (esArrendamiento(p)) next.residual_pct = p.residual_min_pct ?? "";
      // Plazo: si el producto declara plazos permitidos, usa el primero
      const plazosOk = Array.isArray(p.plazos_permitidos) ? p.plazos_permitidos : null;
      if (plazosOk && plazosOk.length > 0) next.plazo = Number(plazosOk[0]);
      else if (p.plazo_max != null && p.plazo_max !== "") next.plazo = Math.min(Number(prev.plazo || 12), Number(p.plazo_max));
      return next;
    });
  };

  const rangoTasa = useMemo(() => {
    if (!productoSel) return { min: null, max: null, label: null };
    const min = productoSel.tasa_ordinaria_min ?? null;
    const max = productoSel.tasa_ordinaria_max ?? null;
    return { min, max, label: formatRangoPct(min, max) };
  }, [productoSel]);

  const rangoMonto = useMemo(() => {
    if (!productoSel) return { min: null, max: null, label: null };
    const min = productoSel.monto_min ?? null;
    const max = productoSel.monto_max ?? null;
    return { min, max, label: formatRangoMonto(min, max) };
  }, [productoSel]);

  const tasaFueraDeRango = productoSel && !dentroDeRango(form.tasa_ordinaria, rangoTasa.min, rangoTasa.max);
  const montoFueraDeRango = productoSel && !dentroDeRango(form.monto, rangoMonto.min, rangoMonto.max);

  // Plazos permitidos por producto (si aplica)
  const plazosPermitidos = useMemo(() => {
    if (!productoSel) return null;
    const arr = Array.isArray(productoSel.plazos_permitidos) ? productoSel.plazos_permitidos : null;
    if (arr && arr.length > 0) return arr.map(Number);
    return null;
  }, [productoSel]);
  const plazoFueraDeLista = productoSel && plazosPermitidos && !plazosPermitidos.includes(Number(form.plazo));

  const calcular = async () => {
    setLoading(true);
    try {
      const payload = {
        ...form,
        monto: Number(form.monto),
        plazo: Number(form.plazo),
        tasa_ordinaria: Number(form.tasa_ordinaria),
        comision: Number(form.comision),
        iva: Number(form.iva),
        comision_apertura_tipo: form.comision_apertura_tipo,
        ancla_pago: form.ancla_pago || undefined,
      };
      if (productoSel) payload.producto_id = productoSel.id;
      const { data } = await api.post("/simulador", esFx ? payloadFactoraje(form, productoSel)
        : esArr ? payloadArrendamiento(form, productoSel) : payload);
      setResult(data);
    } catch (e) {
      const msg = e?.response?.data?.detail || "No se pudo calcular la simulación";
      toast.error(typeof msg === "string" ? msg : "No se pudo calcular la simulación");
    } finally {
      setLoading(false);
    }
  };

  const guardar = async () => {
    try {
      await api.post("/simulador/guardar", { ...form, cliente, producto_id: productoSel?.id, resumen: result && {
        pago_estimado: result.pago_estimado, cuota_credito_fija: result.cuota_credito_fija,
        pago_total_fijo: result.pago_total_fijo,
        total_pagar: result.total_pagar,
        cat: result.cat,
        neto_desembolso: result.neto_desembolso,
      } });
      toast.success("Simulación guardada como solicitud (Prospecto)");
    } catch (e) {
      toast.error("No se pudo guardar");
    }
  };

  const descargarPDF = async () => {
    setDownloadingPdf(true);
    try {
      const payload = {
        ...form,
        monto: Number(form.monto),
        plazo: Number(form.plazo),
        tasa_ordinaria: Number(form.tasa_ordinaria),
        comision: Number(form.comision),
        iva: Number(form.iva),
        comision_apertura_tipo: form.comision_apertura_tipo,
        ancla_pago: form.ancla_pago || undefined,
        cliente: cliente || undefined,
      };
      if (productoSel) payload.producto_id = productoSel.id;
      const resp = await api.post("/simulador/pdf",
        esFx ? { ...payloadFactoraje(form, productoSel), cliente: cliente || undefined }
          : esArr ? { ...payloadArrendamiento(form, productoSel), cliente: cliente || undefined } : payload,
        { responseType: "blob" });
      const blob = new Blob([resp.data], { type: "application/pdf" });
      // Nombre desde el header Content-Disposition; si no está, uno por defecto.
      let filename = "simulacion.pdf";
      const cd = resp.headers?.["content-disposition"] || resp.headers?.get?.("content-disposition");
      const m = cd && /filename="?([^"]+)"?/i.exec(cd);
      if (m) filename = m[1];
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("PDF descargado");
    } catch (e) {
      const msg = e?.response?.data?.detail || "No se pudo generar el PDF";
      toast.error(typeof msg === "string" ? msg : "No se pudo generar el PDF");
    } finally {
      setDownloadingPdf(false);
    }
  };

  return (
    <div>
      <PageHeader title="Simulador de crédito" subtitle="Amortización francesa (cuota fija de capital + interés) o alemana (capital fijo); el IVA sobre intereses se traslada por separado" />
      <div className="grid grid-cols-1 gap-6 p-6 md:p-8 lg:grid-cols-3">
        <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
          <h3 className="mb-4 flex items-center gap-2 font-heading text-sm font-semibold text-slate-700">
            <Calculator className="h-4 w-4 text-primary" /> Parámetros
          </h3>
          <div className="space-y-3">
            {/* Selector de producto */}
            <div>
              <Label className="mb-1 block text-xs font-medium text-slate-600">Producto de crédito</Label>
              <Select value={productoId} onValueChange={onProductoChange}>
                <SelectTrigger data-testid="sim-producto"><SelectValue placeholder="Sin producto (simulación libre)" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={SIN_PRODUCTO} data-testid="sim-producto-opt-libre">
                    Sin producto (simulación libre)
                  </SelectItem>
                  {productos.map((p) => (
                    <SelectItem key={p.id} value={p.id} data-testid={`sim-producto-opt-${p.id}`}>
                      {p.nombre}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {productoSel && (
                <p data-testid="sim-producto-hint" className="mt-1 text-[11px] text-slate-500">
                  Aplica reglas del producto: tasa {rangoTasa.label || "—"} · monto {rangoMonto.label || "—"} ·
                  cobro comisión {String(productoSel.comision_cobro_modo || "descontada")}.
                </p>
              )}
            </div>

            {!productoSel && (
              <div data-testid="sim-aviso-sin-producto"
                className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span>
                  Simulación libre: no está atada a un producto. Al crear la solicitud real, el sistema aplicará
                  los rangos y reglas del producto seleccionado.
                </span>
              </div>
            )}

            {esFx ? <FactorajeSimForm form={form} set={set} producto={productoSel} />
              : esArr ? <ArrendamientoSimForm form={form} set={set} producto={productoSel} /> : (
            <>
            <div>
              <Label className="mb-1 block text-xs font-medium text-slate-600">Tipo de amortización</Label>
              <Select value={form.tipo_amortizacion} onValueChange={(v) => set("tipo_amortizacion", v)}>
                <SelectTrigger data-testid="sim-tipo"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="francesa_total_fijo">Francesa — Pago TOTAL fijo (IVA incluido)</SelectItem>
                  <SelectItem value="francesa">Francesa clásica (cuota fija)</SelectItem>
                  <SelectItem value="alemana">Alemana (capital fijo)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.tipo_amortizacion === "francesa_total_fijo" && (
              <div>
                <Label className="mb-1 block text-xs font-medium text-slate-600">Redondeo del pago total</Label>
                <Select value={form.redondeo_total_fijo} onValueChange={(v) => set("redondeo_total_fijo", v)}>
                  <SelectTrigger data-testid="sim-redondeo"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="centena">Centena hacia arriba (mercado)</SelectItem>
                    <SelectItem value="decena">Decena hacia arriba</SelectItem>
                    <SelectItem value="peso">Peso hacia arriba</SelectItem>
                    <SelectItem value="sin">Sin redondeo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            <div>
              <Label className="mb-1 block text-xs font-medium text-slate-600">
                Monto {rangoMonto.label && <span className="ml-1 text-slate-400 font-normal">({rangoMonto.label})</span>}
              </Label>
              <div className={montoFueraDeRango ? "rounded-md ring-1 ring-red-400" : ""}>
                <MoneyInput data-testid="sim-monto" value={form.monto} onChange={(v) => set("monto", v)} />
              </div>
              {montoFueraDeRango && (
                <p data-testid="sim-monto-fuera-rango" className="mt-1 text-[11px] text-red-600">
                  Monto fuera del rango del producto ({rangoMonto.label}).
                </p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="mb-1 block text-xs font-medium text-slate-600" data-testid="sim-plazo-label">Plazo — {etiquetaPeriodicidad(form.periodicidad)}</Label>
                {plazosPermitidos ? (
                  <Select value={String(form.plazo)} onValueChange={(v) => set("plazo", Number(v))}>
                    <SelectTrigger data-testid="sim-plazo"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {plazosPermitidos.map((pl) => (
                        <SelectItem key={pl} value={String(pl)} data-testid={`sim-plazo-opt-${pl}`}>{pl}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input data-testid="sim-plazo" type="number" value={form.plazo} onChange={(e) => set("plazo", e.target.value)} />
                )}
                {plazoFueraDeLista && (
                  <p data-testid="sim-plazo-fuera-lista" className="mt-1 text-[11px] text-red-600">
                    Plazo no permitido por el producto.
                  </p>
                )}
              </div>
              <div>
                <Label className="mb-1 block text-xs font-medium text-slate-600">Periodicidad</Label>
                <Select value={form.periodicidad} onValueChange={(v) => setForm((p) => ({ ...p, periodicidad: v, ancla_pago: "" }))}
                  disabled={!!productoSel?.periodicidad}>
                  <SelectTrigger data-testid="sim-periodicidad"><SelectValue /></SelectTrigger>
                  <SelectContent>{["Semanal", "Quincenal", "Mensual"].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label className="mb-1 block text-xs font-medium text-slate-600">Ancla de pago (opcional)</Label>
              <AnclaPagoSelect periodicidad={form.periodicidad} value={form.ancla_pago}
                onChange={(v) => set("ancla_pago", v)} testid="sim-ancla" />
              <p className="mt-1 text-[11px] text-slate-400">
                Fecha fija de pago; el primer pago cubre solo el proporcional entre el desembolso y la primera ancla.
              </p>
            </div>
            </>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="mb-1 block text-xs font-medium text-slate-600">
                  Tasa ordinaria (% anual)
                  {rangoTasa.label && <span className="ml-1 text-slate-400 font-normal">({rangoTasa.label})</span>}
                </Label>
                <Input data-testid="sim-tasa" type="number" value={form.tasa_ordinaria}
                  onChange={(e) => set("tasa_ordinaria", e.target.value)}
                  className={tasaFueraDeRango ? "border-red-400 focus:ring-red-400" : ""} />
                {tasaFueraDeRango && (
                  <p data-testid="sim-tasa-fuera-rango" className="mt-1 text-[11px] text-red-600">
                    Tasa fuera del rango del producto ({rangoTasa.label}).
                  </p>
                )}
              </div>
              <div>
                <Label className="mb-1 block text-xs font-medium text-slate-600">Tipo de comisión</Label>
                <Select value={form.comision_apertura_tipo} onValueChange={(v) => set("comision_apertura_tipo", v)}>
                  <SelectTrigger data-testid="sim-comision-tipo"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="porcentaje">Porcentaje (%)</SelectItem>
                    <SelectItem value="monto">Monto fijo ($)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label className="mb-1 block text-xs font-medium text-slate-600">
                {form.comision_apertura_tipo === "monto" ? "Comisión apertura ($ fijo)" : "Comisión apertura (%)"}
              </Label>
              <MoneyInput data-testid="sim-comision" value={form.comision} onChange={(v) => set("comision", v)} />
            </div>

            {!esArr && (
            <div>
              <Label className="mb-1 block text-xs font-medium text-slate-600">Cobro de la comisión</Label>
              <Select value={form.comision_cobro_modo} onValueChange={(v) => set("comision_cobro_modo", v)}
                disabled={!!productoSel?.comision_cobro_modo}>
                <SelectTrigger data-testid="sim-comision-modo"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="descontada">Descontada del desembolso</SelectItem>
                  <SelectItem value="primera_cuota">Financiada — cobro en la 1ª cuota</SelectItem>
                  <SelectItem value="diferida">Financiada — diferida en tabla</SelectItem>
                </SelectContent>
              </Select>
              {productoSel?.comision_cobro_modo && (
                <p className="mt-1 text-[11px] text-slate-400">Definido por el producto.</p>
              )}
            </div>
            )}

            {!esArr && (
            <div>
              <Label className="mb-1 block text-xs font-medium text-slate-600">IVA (%)</Label>
              <Input data-testid="sim-iva" type="number" value={form.iva} onChange={(e) => set("iva", e.target.value)} />
            </div>
            )}

            <Button data-testid="sim-calcular" onClick={calcular} disabled={loading}
              className="w-full bg-primary hover:bg-sidebar-hover">
              {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Calculator className="mr-2 h-4 w-4" />} Calcular
            </Button>
          </div>
        </div>

        <div className="lg:col-span-2">
          {!result ? (
            <div className="flex h-full min-h-[300px] flex-col items-center justify-center rounded-lg border border-dashed border-slate-300 bg-white p-8 text-center text-slate-400">
              <Calculator className="mb-3 h-10 w-10" strokeWidth={1.5} />
              <p className="text-sm">Configura los parámetros y presiona “Calcular” para ver la tabla de amortización.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {result.sin_producto && (
                <div data-testid="sim-result-aviso-sin-producto"
                  className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>
                    Esta simulación NO está atada a un producto. Al crear la solicitud real, la tasa, comisión,
                    modo de cobro y rangos se validarán contra el producto seleccionado en originación.
                  </span>
                </div>
              )}

              {result.producto_nombre && (
                <p data-testid="sim-result-producto" className="text-xs text-slate-500">
                  Producto: <b className="text-slate-800">{result.producto_nombre}</b> · IVA intereses{" "}
                  <b className="text-slate-800">{result.iva_intereses_credito}</b> · Cobro comisión{" "}
                  <b className="text-slate-800">{result.comision_cobro_modo}</b>
                </p>
              )}

              {result.factoraje ? <FactorajeSimKpis result={result} />
                : result.arrendamiento ? <ArrendamientoSimKpis result={result} /> : (
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
                {[
                  result.pago_total_fijo != null
                    ? { l: "Pago total fijo (capital + interés + IVA)", v: mxn(result.pago_total_fijo), tid: "sim-kpi-cuota" }
                    : result.cuota_credito_fija != null
                    ? { l: "Cuota fija (capital + interés)", v: mxn(result.cuota_credito_fija), tid: "sim-kpi-cuota" }
                    : { l: "Cuota inicial (capital + interés)", v: mxn(result.tabla?.find((r) => !r.es_proporcional)?.cuota_credito), tid: "sim-kpi-cuota" },
                  { l: "Neto desembolso", v: mxn(result.neto_desembolso), tid: "sim-kpi-neto" },
                  {
                    l: "CAT (%)",
                    v: result.cat != null ? `${Number(result.cat).toFixed(2)}%` : "—",
                    tid: "sim-kpi-cat",
                  },
                  { l: "Total a pagar", v: mxn(result.total_pagar), tid: "sim-kpi-total-pagar" },
                  { l: "Total interés", v: mxn(result.total_interes), tid: "sim-kpi-total-interes" },
                  { l: "Total IVA", v: mxn(result.total_iva), tid: "sim-kpi-total-iva" },
                  { l: "Total 1er periodo (cuota + IVA)", v: mxn(result.pago_estimado), tid: "sim-kpi-total-periodo" },
                ].map((x) => (
                  <div key={x.l} data-testid={x.tid} className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm">
                    <p className="text-[11px] uppercase tracking-wide text-slate-500">{x.l}</p>
                    <p className="mt-1 font-heading text-lg font-semibold text-slate-900 tabular-nums">{x.v}</p>
                  </div>
                ))}
              </div>
              )}

              {result.pago_total_fijo != null && (
                <p data-testid="sim-nota-total-fijo" className="text-[11px] text-slate-500">
                  Pago <b>TOTAL</b> fijo: el cliente paga siempre <b>{mxn(result.pago_total_fijo)}</b> (capital +
                  interés + IVA). El IVA es el {form.iva}% del interés real devengado de cada periodo (no se
                  prorratea) y el capital absorbe la diferencia. La primera cuota puede variar si el periodo es
                  irregular y la última ajusta los centavos de liquidación.
                </p>
              )}

              {!result.arrendamiento && result.pago_total_fijo == null && result.cuota_credito_fija != null && Number(result.total_iva) > 0 && (
                <p data-testid="sim-nota-cuota-fija" className="text-[11px] text-slate-500">
                  Sistema francés: la <b>Cuota</b> (capital + interés) es constante todo el plazo. El IVA se
                  traslada por separado sobre el interés devengado (decrece), por lo que el <b>Total</b> del
                  periodo varía. La última cuota puede incluir un ajuste de centavos de liquidación.
                </p>
              )}

              {result.dias_proporcional != null && (
                <div data-testid="sim-leyenda-proporcional"
                  className="flex items-start gap-2 rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-sm text-sky-900">
                  <CalendarClock className="mt-0.5 h-4 w-4 shrink-0" />
                  <div>
                    <p className="font-semibold">
                      Primer pago proporcional por {result.dias_proporcional} días — vence el {fdate(result.fecha_primer_pago)}.
                    </p>
                    <p>
                      {anclaLabel(result.periodicidad, result.ancla_pago)} · interés proporcional {mxn(result.interes_proporcional)}
                      {result.iva_interes_proporcional ? ` + IVA ${mxn(result.iva_interes_proporcional)}` : ""}
                      {result.primer_pago_modalidad === "cuota_completa"
                        ? " incluido en la primera cuota completa."
                        : " (el capital inicia en la siguiente cuota)."}
                    </p>
                  </div>
                </div>
              )}

              <div className="flex items-center gap-2">
                <Input placeholder="Nombre del cliente (opcional)" value={cliente} onChange={(e) => setCliente(e.target.value)} className="max-w-xs" />
                <Button data-testid="sim-guardar" variant="outline" onClick={guardar}>
                  <Save className="mr-1.5 h-4 w-4" /> Guardar como solicitud
                </Button>
                <Button data-testid="sim-descargar-pdf" variant="outline" onClick={descargarPDF} disabled={downloadingPdf}>
                  {downloadingPdf
                    ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                    : <FileDown className="mr-1.5 h-4 w-4" />}
                  Descargar PDF
                </Button>
              </div>

              {result.factoraje ? (
                <TablaFactoraje tabla={result.tabla} dataTestid="sim-tabla-factoraje" conEstatus={false} />
              ) : result.arrendamiento ? (
                <TablaArrendamiento tabla={result.tabla} dataTestid="sim-tabla-arrendamiento" conEstatus={false} />
              ) : (
              <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
                <div className="max-h-[420px] overflow-y-auto">
                  <table className="w-full text-left text-sm">
                    <thead className="sticky top-0 bg-slate-50">
                      <tr className="text-xs uppercase tracking-wide text-slate-500">
                        <th className="px-3 py-2">#</th>
                        <th className="px-3 py-2">Fecha</th>
                        <th className="px-3 py-2 text-right">Capital</th>
                        <th className="px-3 py-2 text-right">Interés</th>
                        <th className="px-3 py-2 text-right">IVA</th>
                        <th className="px-3 py-2 text-right">Cuota</th>
                        <th className="px-3 py-2 text-right">Total</th>
                        <th className="px-3 py-2 text-right">Saldo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.tabla.map((row) => (
                        <tr key={row.no} className={`border-t border-slate-100 tabular-nums${row.es_proporcional ? " bg-sky-50/60" : ""}`}>
                          <td className="px-3 py-2 text-slate-500">
                            {row.no}
                            {row.es_proporcional && (
                              <span className="ml-1.5 rounded bg-sky-100 px-1.5 py-0.5 text-[10px] font-semibold text-sky-700">
                                Proporcional · {row.dias_proporcional} días
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-slate-500">{fdate(row.fecha_pago)}</td>
                          <td className="px-3 py-2 text-right font-mono">{mxn(row.capital)}</td>
                          <td className="px-3 py-2 text-right font-mono">{mxn(row.interes)}</td>
                          <td className="px-3 py-2 text-right font-mono">{mxn(row.iva_interes)}</td>
                          <td className={`px-3 py-2 text-right font-mono${result.pago_total_fijo != null ? "" : " font-semibold"}`}>{mxn(row.cuota_credito ?? Math.round((Number(row.capital || 0) + Number(row.interes || 0)) * 100) / 100)}</td>
                          <td className={`px-3 py-2 text-right font-mono${result.pago_total_fijo != null ? " font-semibold" : ""}`}>{mxn(row.total_periodo ?? row.pago)}</td>
                          <td className="px-3 py-2 text-right font-mono text-slate-500">{mxn(row.saldo_insoluto)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
